import { AppRouteRecordRaw } from '../types';
import { PROMO_CAMPAIGN_USER_GROUPS } from '@/constants/promo-campaign-access';

const Body = () => import('@/components/body.vue');
const VoucherCampaignStatistics = () =>
  import('@/pages/ryd-marketing/voucher-campaigns/VoucherCampaignStatistics.vue');
const VoucherCampaignOrderStatistics = () =>
  import('@/pages/ryd-marketing/voucher-campaigns/VoucherCampaignOrderStatistics.vue');
const VoucherCampaignRegistrations = () =>
  import('@/pages/ryd-marketing/voucher-campaigns/VoucherCampaignRegistrations.vue');
const VoucherCampaignRegistrationDetail = () =>
  import('@/pages/ryd-marketing/voucher-campaigns/VoucherCampaignRegistrationDetail.vue');
const VoucherCampaignHowItWorks = () =>
  import('@/pages/ryd-marketing/voucher-campaigns/VoucherCampaignHowItWorks.vue');

/**
 * Operator screens for the public co-branded voucher campaigns.
 *
 * Top-level rather than under `/ryd-marketing` because the campaign partner uses
 * them too: Stellantis reviews their own registrations here, and the
 * `/ryd-marketing` tree is gated to ryd staff. The pages themselves still live
 * under `pages/ryd-marketing/voucher-campaigns/` — one implementation, one URL,
 * two audiences.
 *
 * The public landing pages are at `/promo/:slug` and are deliberately outside
 * this (authenticated) tree.
 */
export const voucherCampaignsRoutes: AppRouteRecordRaw[] = [
  {
    path: '/voucher-campaigns',
    component: Body,
    meta: {
      requiresAuth: true,
      requiredUserGroup: PROMO_CAMPAIGN_USER_GROUPS,
      breadcrumb: 'Discount Campaigns',
    },
    children: [
      {
        path: 'statistics',
        name: 'voucherCampaignStatistics',
        component: VoucherCampaignStatistics,
        meta: {
          title: 'Discount Campaign Landing Page Statistics | ryd Portal',
          breadcrumb: 'Landing Page Statistics',
        },
      },
      {
        // What the approved customers ordered, and which discounts they used.
        path: 'order-statistics',
        name: 'voucherCampaignOrderStatistics',
        component: VoucherCampaignOrderStatistics,
        meta: {
          title: 'Discount Campaign Order Statistics | ryd Portal',
          breadcrumb: 'Order Statistics',
        },
      },
      {
        path: 'registrations',
        name: 'voucherCampaignRegistrations',
        component: VoucherCampaignRegistrations,
        meta: {
          title: 'Discount Campaign Registrations | ryd Portal',
          breadcrumb: 'Campaign Registrations',
        },
      },
      {
        // One customer, as its own page so the link can be shared and reopened.
        // The id is `<campaign slug>__<hash of the address>`: it identifies the
        // row without putting a customer's email in a URL or a server log.
        path: 'registrations/:registrationId',
        name: 'voucherCampaignRegistrationDetail',
        component: VoucherCampaignRegistrationDetail,
        props: true,
        meta: {
          title: 'Campaign Registration | ryd Portal',
          breadcrumb: 'Registration',
        },
      },
      {
        // The documentation page: the whole process with its animated diagrams.
        // Kept out of the operator screens so their usage notes can stay short
        // and point here instead.
        path: 'how-it-works',
        name: 'voucherCampaignHowItWorks',
        component: VoucherCampaignHowItWorks,
        meta: {
          title: 'How The Discount Campaign Works | ryd Portal',
          breadcrumb: 'How It Works',
        },
      },
    ],
  },
];
